import { Figure } from '@/components/Figure'
import { ProgressRing } from '@/components/ProgressRing'
import { getExercise } from '@/data/exercises'
import type { AmrapPhase } from '@/domain/workout'
import type { SessionRecord } from '@/domain/session'
import { cx } from '@/lib/cx'
import { mmss } from '@/lib/format'
import { audioCues } from '@/services/audioCues'
import { selectRounds, useSessionStore } from '@/store/sessionStore'
import type { PhaseRuntime } from './usePhaseRunner'
import styles from './workout.module.css'

interface AmrapViewProps {
  phase: AmrapPhase
  runtime: PhaseRuntime
  /** Marca previa a batir, cuando la semana es un test. */
  benchmark?: SessionRecord | null
}

/** Reloj a la baja: el alumno cuenta sus rondas tocando el botón grande. */
export function AmrapView({ phase, runtime, benchmark }: AmrapViewProps) {
  const rounds = useSessionStore(selectRounds)
  const addRound = useSessionStore((state) => state.addRound)
  const removeRound = useSessionStore((state) => state.removeRound)

  const urgent = runtime.remaining <= 10

  return (
    <div className={styles.work}>
      <ProgressRing progress={runtime.progress} overall={runtime.overall} compact>
        <span className={cx(styles.clockInner, urgent && styles.clockWarning)}>
          {mmss(runtime.remaining)}
        </span>
      </ProgressRing>

      <div className={styles.status}>
        <div className={styles.statusLabel}>Tantas rondas como puedas</div>
      </div>

      <div className={styles.tasks}>
        {phase.movements.map((movement, index) => {
          const exercise = getExercise(movement.exerciseId)
          return (
            <div key={`${movement.exerciseId}-${index}`} className={styles.task}>
              <b className={styles.taskReps}>{movement.reps}</b>
              <div className={styles.taskName}>
                {exercise.name}
                <i className={styles.taskEs}>{exercise.nameEs}</i>
              </div>
              {/* El monigote recuerda el movimiento sin tener que leer. */}
              <div className={styles.taskFigure}>
                <Figure exerciseId={movement.exerciseId} />
              </div>
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className={styles.roundTap}
        onClick={() => {
          addRound()
          audioCues.play('tick')
        }}
      >
        <span className={styles.roundNumber}>{rounds}</span>
        <span className={styles.roundLabel}>
          <b>Rondas</b>
          <span>Toca al acabar cada ronda</span>
        </span>
      </button>

      <button type="button" className={styles.roundUndo} onClick={removeRound}>
        − Quitar una
      </button>

      {benchmark && (
        <div className={styles.benchmark}>
          Marca anterior · {benchmark.rounds} rondas + {benchmark.reps} reps
        </div>
      )}
    </div>
  )
}
